import { IconHistoryOutline18 as History } from "nucleo-ui-outline-18"

import {
	Badge,
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { formatArgsJson } from "../../../settings-pages/mcp-activity-json"
import { SettingItem } from "../../../settings-switches/setting-item"
import { SettingsContainer } from "../../../settings-switches/settings-container"

export const API_REQUESTS_PATH = "/settings/integrations/api/requests"

const REFUSAL_LABELS: Record<string, string> = {
	unknown_tool: "Unknown tool",
	scope_denied: "Scope denied",
	forwarder_error: "Failed",
}

export type ActivityRow =
	RouterOutputs["apiAnalytics"]["workspaceApiActivity"][number]

// The full history is its own page; the API page keeps one quiet row to it.
export function ApiActivityBlock() {
	return (
		<SettingsContainer className="mt-8">
			<SettingItem
				Icon={History}
				title="Requests"
				description="Every call to the public API and MCP."
				link={API_REQUESTS_PATH}
			/>
		</SettingsContainer>
	)
}

export function ActivityDetailSheet({
	row,
	open,
	onOpenChange,
}: {
	row: ActivityRow
	open: boolean
	onOpenChange: (next: boolean) => void
}) {
	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex w-full flex-col sm:max-w-xl">
				<SheetHeader>
					<SheetTitle className="flex items-center gap-2">
						<span className="truncate font-mono text-sm">
							{row.toolName ?? `${row.method} ${row.path}`}
						</span>
						<Badge variant={isFailure(row) ? "red" : "green"} size="sm">
							{statusLabel(row)}
						</Badge>
					</SheetTitle>
				</SheetHeader>

				<div className="min-h-0 flex-1 overflow-y-auto pb-6">
					<dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-2 text-xs">
						<Detail label="Source">
							{row.source === "mcp" ? "MCP tool call" : "Direct API call"}
						</Detail>
						{row.method && row.path && (
							<Detail label="Endpoint">
								<span className="font-mono">
									{row.method} {row.path}
								</span>
							</Detail>
						)}
						{row.apiKeyLabel && (
							<Detail label="API key">{row.apiKeyLabel}</Detail>
						)}
						<Detail label="When">
							{new Date(row.createdAt).toLocaleString()}
						</Detail>
						<Detail label="Duration">{row.durationMs} ms</Detail>
						{row.statusCode !== null && (
							<Detail label="HTTP status">{row.statusCode}</Detail>
						)}
						{row.errorCode && <Detail label="Error">{row.errorCode}</Detail>}
						<Detail label="Request id">
							<span className="font-mono">{row.requestId}</span>
						</Detail>
					</dl>

					{row.errorMessage && (
						<p className="mt-4 text-xs text-red-600 dark:text-red-400">
							{row.errorMessage}
						</p>
					)}

					{row.source === "mcp" && (
						<div className="mt-5">
							<div className="text-xs font-medium text-primary">Arguments</div>
							{row.args ? (
								<pre className="mt-2 overflow-x-auto rounded-lg bg-gray-100 p-3 font-mono text-xs leading-relaxed text-primary dark:bg-gray-800">
									{formatArgsJson(row.args)}
								</pre>
							) : (
								<p className="mt-2 text-xs text-secondary">
									{row.argKeys.length > 0
										? `Too large to store. Keys: ${row.argKeys.join(", ")}.`
										: "No arguments."}
								</p>
							)}
						</div>
					)}
				</div>
			</SheetContent>
		</Sheet>
	)
}

function Detail({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<>
			<dt className="text-secondary">{label}</dt>
			<dd className="min-w-0 break-words text-primary">{children}</dd>
		</>
	)
}

export function isFailure(row: ActivityRow): boolean {
	return row.statusCode === null ? true : row.statusCode >= 400
}

export function statusLabel(row: ActivityRow): string {
	if (row.statusCode !== null) return String(row.statusCode)
	return REFUSAL_LABELS[row.errorCode ?? ""] ?? "Refused"
}
